/**
 * Trust gate for postMessage traffic coming from the embedding WordPress page.
 *
 * The parent origin is not a value we can hardcode: the same bundle is embedded
 * on tcgservices.com, region10rams.org, calstrs403bcomply.com, tasbo.org and
 * every other client site, which is exactly why the original origin check in
 * App.js was commented out rather than filled in. So the check is two layers:
 *
 *   1. `event.source === window.parent` - always enforced. Only the window that
 *      actually embeds us can drive our navigation; an unrelated opener, popup
 *      or sibling frame cannot, whatever origin it claims.
 *   2. REACT_APP_ALLOWED_PARENT_ORIGINS - optional comma-separated allowlist.
 *      Set it per deployment to pin the embed to known domains; leave it unset
 *      for the shared build that has to serve every client domain.
 */

const ALLOWED_PARENT_ORIGINS = (
  process.env.REACT_APP_ALLOWED_PARENT_ORIGINS || ""
)
  .split(",")
  .map((origin) => origin.trim())
  .filter(Boolean);

export function isTrustedParentMessage(event) {
  // Standalone (non-iframe) context: window.parent is window itself, so there
  // is no parent to trust and nothing legitimate sends us messages.
  if (window.parent === window) return false;
  if (event.source !== window.parent) return false;

  if (
    ALLOWED_PARENT_ORIGINS.length > 0 &&
    !ALLOWED_PARENT_ORIGINS.includes(event.origin)
  ) {
    return false;
  }

  return true;
}

// The characters these routes actually use: path segments plus the
// percent-encoding that plan labels arrive in, and an optional query/hash.
// Being a whitelist, this also drops control characters (NUL, tab, newline),
// which are otherwise used to smuggle a leading "//" past prefix checks.
const SAFE_ROUTE = /^\/[A-Za-z0-9\-._~%!$&'()*+,;=:@/?#]*$/;

/**
 * A route handed to us over ROUTE_CHANGE is only ever an in-app path, so reject
 * anything that could be re-read as an absolute or protocol-relative URL.
 *
 * Under HashRouter these land in the fragment rather than the document URL, so
 * "//evil.com" does not currently leave the origin - this is defence in depth
 * against the react-router open-redirect advisories (CVE-2025-68470 and its
 * backslash bypass, GHSA-wrjc-x8rr-h8h6) becoming reachable if this app is ever
 * switched to BrowserRouter, and against untrusted input reaching the router
 * at all.
 */
export function isSafeInternalRoute(route) {
  if (typeof route !== "string" || route === "") return false;
  if (route.length > 512) return false;

  // Must be app-relative...
  if (route.charAt(0) !== "/") return false;
  // ...but not protocol-relative ("//host") or its backslash variants, which
  // browsers and the router normalise back to "//host".
  if (route.charAt(1) === "/" || route.charAt(1) === "\\") return false;
  if (route.indexOf("\\") !== -1) return false;

  return SAFE_ROUTE.test(route);
}
